import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Switch,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";

type PinOption = {
  id: string;
  name: string;
  isDefault?: boolean;
};

export const UpdatePinDialog = ({
  open,
  onClose,
  onConfirm,
  pins,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (pinId: string, setAsDefault: boolean, newName?: string) => void;
  pins: PinOption[];
}) => {
  const [selectedPinId, setSelectedPinId] = useState<string>("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [renameEnabled, setRenameEnabled] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset selection and toggle when dialog opens
      setSelectedPinId("");
      setSetAsDefault(false);
      setNewName("");
      setError("");
      setRenameEnabled(false);
    }
  }, [open, pins]);

  useEffect(() => {
    if (!open) return;
    const selectedPin = pins.find(
      (p) => String(p.id) === String(selectedPinId)
    );
    if (selectedPin) {
      setSetAsDefault(Boolean(selectedPin.isDefault));
      // Pre-fill rename field with current name, but only when rename is enabled
      if (renameEnabled) {
        setNewName(selectedPin.name);
      } else {
        setNewName("");
      }
      setError("");
    } else {
      setSetAsDefault(false);
      setNewName("");
      setError("");
    }
  }, [selectedPinId, pins, open, renameEnabled]);

  const validateNewName = (nameToValidate?: string): string | null => {
    if (!renameEnabled) {
      // If rename is not enabled, skip validation and treat as no rename
      setError("");
      return "";
    }

    if (!selectedPinId) {
      // No pin selected, we don't validate yet
      return null;
    }

    const selectedPin = pins.find(
      (p) => String(p.id) === String(selectedPinId)
    );
    if (!selectedPin) {
      return null;
    }

    const currentPinName = selectedPin.name;
    const name = nameToValidate ?? newName;
    const trimmedName = name.trim();

    // If user cleared or left name unchanged, treat as "no rename"
    if (!trimmedName || trimmedName === currentPinName) {
      setError("");
      return trimmedName;
    }

    // Check if name already exists (excluding current pin) - case-insensitive
    const otherPinNames = pins
      .map((p) => p.name)
      .filter((name) => name !== currentPinName);
    const normalizedExisting = otherPinNames.map((n) => n.toLowerCase().trim());
    if (normalizedExisting.includes(trimmedName.toLowerCase())) {
      setError("A pin with this name already exists");
      return null;
    }

    if (trimmedName.length > 50) {
      setError("Pin name must be 50 characters or less");
      return null;
    }

    // Validation passed - clear any existing error
    setError("");
    return trimmedName;
  };

  const handleConfirm = () => {
    if (!selectedPinId) return;

    const validatedName = validateNewName();
    if (validatedName === null) {
      // Validation failed
      return;
    }

    const selectedPin = pins.find(
      (p) => String(p.id) === String(selectedPinId)
    );
    const currentPinName = selectedPin?.name ?? "";

    const effectiveNewName =
      renameEnabled && validatedName && validatedName !== currentPinName
        ? validatedName
        : undefined;

    onConfirm(selectedPinId, setAsDefault, effectiveNewName);
  };

  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>Update Existing Pin</Box>
        <IconButton
          onClick={onClose}
          sx={{
            p: 0,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <FormControl component="fieldset" sx={{ width: "100%", mt: 1 }}>
            <FormLabel component="legend">
              Choose a pin to update with your current view settings:
            </FormLabel>
            {pins.length <= 1 ? (
              <RadioGroup
                value={selectedPinId}
                onChange={(event) => setSelectedPinId(event.target.value)}
              >
                {pins.map((pin) => (
                  <FormControlLabel
                    key={pin.id}
                    value={String(pin.id)}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography>{pin.name}</Typography>
                        {pin.isDefault && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            (default)
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            ) : (
              <Select
                value={selectedPinId}
                onChange={(event) =>
                  setSelectedPinId(String(event.target.value))
                }
                displayEmpty
                variant="standard"
                size="small"
                fullWidth
                sx={{
                  mt: 1,
                  "& .MuiSelect-select": {
                    paddingRight: "32px !important",
                  },
                }}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Typography color="text.secondary">
                        Select an existing pin
                      </Typography>
                    );
                  }
                  const pin = pins.find((p) => String(p.id) === String(value));
                  if (!pin) {
                    return (
                      <Typography color="text.secondary">
                        Select an existing pin
                      </Typography>
                    );
                  }
                  return (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography>{pin.name}</Typography>
                      {pin.isDefault && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          (default)
                        </Typography>
                      )}
                    </Box>
                  );
                }}
              >
                <MenuItem disabled value="">
                  <Typography color="text.secondary">
                    Select an existing pin
                  </Typography>
                </MenuItem>
                {pins.map((pin) => (
                  <MenuItem key={pin.id} value={String(pin.id)}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography>{pin.name}</Typography>
                      {pin.isDefault && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          (default)
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                  disabled={!selectedPinId}
                />
              }
              label="Set as default"
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={renameEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setRenameEnabled(enabled);
                    if (!enabled) {
                      setNewName("");
                      setError("");
                    } else if (selectedPinId) {
                      const selectedPin = pins.find(
                        (p) => String(p.id) === String(selectedPinId)
                      );
                      setNewName(selectedPin?.name ?? "");
                    }
                  }}
                  disabled={!selectedPinId}
                />
              }
              label="Rename pin (optional)"
            />
            {renameEnabled && (
              <Box sx={{ mt: 0 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  value={newName}
                  disabled={!selectedPinId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewName(value);
                    if (value.trim()) {
                      validateNewName(value);
                    } else {
                      // Empty means "keep or clear to unnamed" – no validation error
                      setError("");
                    }
                  }}
                  margin="dense"
                  placeholder="Enter a new name or leave blank to keep current"
                  error={!!error}
                  helperText={
                    error ||
                    "If you enter a new name, this pin will be renamed while updating."
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedPinId}
        >
          Update Pin
        </Button>
      </DialogActions>
    </Dialog>
  );
};
